import React from "react";
import {
  ValidationResult,
  ColumnValidationResult,
  ColumnMatch,
} from "../types/api";

interface ColumnValidationWarningProps {
  validationResult: ValidationResult;
  onColumnFix: (originalColumn: string, newColumn: string) => void;
  onIgnoreWarning: () => void;
  onCancel: () => void;
}

export const ColumnValidationWarning: React.FC<
  ColumnValidationWarningProps
> = ({ validationResult, onColumnFix, onIgnoreWarning, onCancel }) => {
  const problemColumns = validationResult.column_results.filter(
    (col) => col.status !== "found",
  );

  if (problemColumns.length === 0) {
    return null;
  }

  const renderSuggestion = (
    columnResult: ColumnValidationResult,
    suggestion: ColumnMatch,
  ) => (
    <button
      key={suggestion.ActualName}
      onClick={() =>
        onColumnFix(columnResult.requested_column, suggestion.ActualName)
      }
      style={{
        display: "block",
        width: "100%",
        padding: "8px 12px",
        marginBottom: "4px",
        backgroundColor: "#e3f2fd",
        border: "1px solid #2196f3",
        borderRadius: "4px",
        cursor: "pointer",
        textAlign: "left",
        fontSize: "14px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#bbdefb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#e3f2fd";
      }}
    >
      <strong>'{suggestion.ActualName}'</strong> (
      {(suggestion.Similarity * 100).toFixed(1)}% similar)
    </button>
  );

  return (
    <div
      style={{
        border: "2px solid #ff9800",
        borderRadius: "8px",
        backgroundColor: "#fff3e0",
        padding: "20px",
        marginBottom: "20px",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <span style={{ fontSize: "24px", marginRight: "10px" }}>⚠️</span>
        <h3 style={{ margin: 0, color: "#e65100" }}>
          Column Names Need Attention
        </h3>
      </div>

      <p style={{ marginBottom: "20px", color: "#bf360c" }}>
        <strong>Important:</strong> Some columns couldn't be found exactly as
        specified. Processing with missing columns could result in{" "}
        <strong>unintended exposure of personal data</strong>. Please review and
        fix the column names below.
      </p>

      {problemColumns.map((columnResult) => (
        <div
          key={columnResult.requested_column}
          style={{
            backgroundColor: "white",
            border: "1px solid #ffcc02",
            borderRadius: "6px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: "#d84315" }}>Column not found:</strong>{" "}
            <code
              style={{
                backgroundColor: "#ffebee",
                padding: "2px 6px",
                borderRadius: "3px",
              }}
            >
              {columnResult.requested_column}
            </code>
          </div>

          {columnResult.status === "similar_found" &&
            columnResult.suggestions &&
            columnResult.suggestions.length > 0 && (
              <div>
                <div
                  style={{
                    marginBottom: "8px",
                    fontWeight: "bold",
                    color: "#1565c0",
                  }}
                >
                  Did you mean one of these?
                </div>
                {columnResult.suggestions
                  .slice(0, 3)
                  .map((suggestion) =>
                    renderSuggestion(columnResult, suggestion),
                  )}
              </div>
            )}

          {columnResult.status === "not_found" && (
            <div style={{ color: "#d32f2f", fontStyle: "italic" }}>
              No similar columns found. Available columns:{" "}
              {validationResult.available_columns.join(", ")}
            </div>
          )}
        </div>
      ))}

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            padding: "10px 20px",
            backgroundColor: "#2196f3",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Fix Column Names
        </button>
        <button
          onClick={onIgnoreWarning}
          style={{
            padding: "10px 20px",
            backgroundColor: "#ff5722",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          ⚠️ Proceed Anyway (Risk: personal-data exposure)
        </button>
      </div>

      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          backgroundColor: "#e8f5e8",
          border: "1px solid #4caf50",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      >
        <strong>💡 Pro tip:</strong> Column matching is case-insensitive and
        ignores extra whitespace. Try: "
        {validationResult.available_columns.slice(0, 3).join('", "')}"
      </div>
    </div>
  );
};
